import { isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	Component,
	DoCheck,
	EventEmitter,
	Inject,
	Input,
	IterableDiffer,
	IterableDiffers,
	OnDestroy,
	OnInit,
	Output,
	PLATFORM_ID
} from '@angular/core';
import { Subscription } from 'rxjs';

import {
	ContainerId,
	IElasticProductDetails,
	IElasticProductDetailsStyle,
	IRecommendationsRequestPurchaseTransaction,
	ProductRecommendationsPageType,
	ProductRecommendationsService
} from '@product-recommendations/data-access';

import { AnalyticsService, DeviceService, EmitEvent, EventBusService, Events, LocalStorageService, Scene7Service, UtilityService, WINDOW } from '@shared/utils/services';
import { BreakPoint, FetchPriority, LoadingStrategy } from '@shared/data-access/enums';
import { CheckoutPaymentPricing } from '@cart-checkout/data-access/interfaces';
import { Chain } from '@shared/data-access/interfaces';
import { ATC_BUTTON_TEXTS } from '@pdp/add-to-cart/data-access';


@Component({
	selector: 'product-recommendations-components-ds-product-recommendations',
	templateUrl: './ds-product-recommendations.component.html',
	styleUrls: ['./ds-product-recommendations.component.scss']
})
export class DSProductRecommendationsComponent implements OnInit, AfterViewInit, OnDestroy, DoCheck {
	/*
	 * INPUT(S)
	 */
	@Input() addTaxAndCost = false;
	@Input() containerID = '';
	@Input() displayName = 'Recommendations';
	@Input() dsRecsDiversityLevel = '';
	@Input() eCodeList: string[] = [];
	@Input() eventType = 'shopping-cart-page-view';
	@Input() hideAddToCart = false;
	@Input() hasGuidVisitor = false;
	@Input() orderPricing: CheckoutPaymentPricing;
	@Input() placementID = 'frequently-bought-together-no-diversity';
	@Input() pageType = 'Cart';
	@Input() priceRerankLevel = 'no-price-reranking';
	@Input() recommendationRefreshEnabled = false;
	@Input() showViewAllLink = false;
	@Input() useCartWindowOverrides = true;
	@Input() enableRepositionRecommendations = false;
	@Input() customLinkID = '';
	@Input() capitalizeTitle = false;
	@Input() productRecommendationsQuickViewEnabled = false;
	@Input() showHeading = true;
	@Input() isPLA = false;
	// HMF inputs
	@Input() homefieldCarouselEnabled = false;
	@Input() showHeader = false;
	@Input() title = '';
	@Input() showSkeletonLoading = false;
	@Input() cardSize?: 'small' | 'medium' | 'large';

	/*
	 * OUTPUT(S)
	 */
	@Output() recommendationsLoaded = new EventEmitter<boolean>();
	@Output() recommendationsErrored = new EventEmitter<void>();

	/*
	 * Public instance properties
	 */
	get showATC(): boolean {
		if (this.hideAddToCart) {
			return false;
		}
		return (
			this.window.quickview?.openProductQuickView !== undefined &&
			this.window.quickview?.openProductQuickView !== null
		);
	}
	breakpoints = [BreakPoint.MEDIUM, BreakPoint.SMALL, BreakPoint.EXTRASMALL];
	eCodeListDiffer: IterableDiffer<any>;
	isFirstLoad = true;
	isMobile: boolean;
	productRecommendations: IElasticProductDetailsStyle[] = [];
	atcButtonTexts = ATC_BUTTON_TEXTS;

	/*
	 * Private instance properties
	 */
	private athleteID: string;
	private dsFilter = 'filterOutOfStockItems';
	private dsPDPlanding = 'PDP_pdplanding';
	private dsPDPYmal = 'PDP_YMAL';
	private dsRecsGCPProject = '';
	private dsRecsProductDisplayLimit = 10;
	private killSwitchSubscription$: Subscription;
	private onAddToCartButtonClickEventName = 'QuickViewClicked';
	private onRecommendationLinkClickEventName = 'RecommendationClicked';
	private onRecommendationsDisplayErrorEventName = 'DCSG_NGX_CART_RECOMMENDATIONS_ERROR';

	constructor(
		@Inject(PLATFORM_ID) protected platformId: any,
		@Inject(WINDOW) private window: Window,
		public chain: Chain,
		public iterableDiffers: IterableDiffers,
		private analyticsService: AnalyticsService,
		private deviceService: DeviceService,
		private eventBusService: EventBusService,
		private localStorageService: LocalStorageService,
		private recommendationsService: ProductRecommendationsService,
		private scene7Service: Scene7Service,
		private utilityService: UtilityService
	) {}

	ngOnInit() {
		// Only do stuff client-side and if there are products
		if (isPlatformBrowser(this.platformId) && this.eCodeList?.length > 0) {
			this.isMobileDevice();
			// Check if global window values should be used
			if (this.useCartWindowOverrides) {
				/* Use global window values */
				// Product display limit
				if (typeof this.window.dsProductDisplayLimit !== 'undefined') {
					this.dsRecsProductDisplayLimit = this.window.dsProductDisplayLimit;
				}

				// Product filter
				if (typeof this.window.dsFilter !== 'undefined') {
					this.dsFilter = this.window.dsFilter;
				}

				// Placement ID
				if (typeof this.window.dsPlacementId !== 'undefined') {
					this.placementID = this.window.dsPlacementId;
				}

				// Display name
				if (typeof this.window.dsDisplayName !== 'undefined') {
					this.displayName = this.window.dsDisplayName;
				}

				// Diversity level
				if (typeof this.window.dsDiversityLevel !== 'undefined') {
					this.dsRecsDiversityLevel = this.window.dsDiversityLevel;
				}

				// GCP Project
				if (typeof this.window.dsRecsGCPProject !== 'undefined') {
					this.dsRecsGCPProject = this.window.dsRecsGCPProject;
				}
			}

			// Initialize eCodeList change detection
			if (this.recommendationRefreshEnabled) {
				this.eCodeListDiffer = this.iterableDiffers.find([]).create(null);
			}

			// Get the athleteID
			this.athleteID = this.getAthleteID();

			// Clear recommendations so the skeleton shows while loading
			if (this.showSkeletonLoading){
				this.setRecsToEmptyForSkeletonLoading();
			}

			// Get recommendations
			this.refreshRecommendations();
			
		} else if (this.showSkeletonLoading && this.isPLA) {
			this.setRecsToEmptyForSkeletonLoading();
		}
		
	}

	/**
	 * @description Set the recommendations to empty for skeleton loading
	 * This will ensure that the product recommendations are not displayed until new data is received
	 */
	setRecsToEmptyForSkeletonLoading(): void {
		// Set recs to nulls
		this.productRecommendations = new Array(8).fill(null);
	}

	ngAfterViewInit(): void {
		// Check to see if QuickView exists
		if (!this.window.quickview?.openProductQuickView) {
			// Set error for analytics Event
			const error =
				'QuickViews Recommendations ATC not visible. window.quickview.openProductQuickView is not defined';
			this.analyticsService.setErrorId(this.onRecommendationsDisplayErrorEventName);
			this.analyticsService.setErrorMessage(`CART_RECOMMENDATIONS:${error}`);

			// Trigger analytics Event
			this.analyticsService.triggerAnalyticsEvent(
				this.onRecommendationsDisplayErrorEventName,
				{
					ErrorMessage: `CART_RECOMMENDATIONS: ${error}`,
					PageType: this.pageType || 'Cart'
				}
			);
		}
		this.isFirstLoad = false;
	}

	// Listens for eCodeListchanges to refresh on item removal or addition based on list content (not reference)
	ngDoCheck(): void {
		if (this.recommendationRefreshEnabled) {
			const eCodeChanges = this.eCodeListDiffer.diff(this.eCodeList);
			if (!this.isFirstLoad && eCodeChanges) {
				this.refreshRecommendations();
			}
		}
	}

	ngOnDestroy(): void {
		if (this.killSwitchSubscription$) {
			// Unsubscribe to killswitch subscription
			this.killSwitchSubscription$.unsubscribe();
		}
	}

	/**
	 * @description
	 * Event handler for when the ATC button is clicked in the product card. It will
	 * trigger an Event to analytics and then open the QuickView panel.
	 *
	 * NOTE: This method can ONLY be called if QuickView is available, because the ATC button will not be visible
	 * if QuickView is not available.
	 *
	 * @param style -- The recommended product style document
	 */
	onAddToCartButtonClick(style: IElasticProductDetailsStyle, index: number) {
		// Trigger analytics Event
		this.analyticsService.triggerAnalyticsEvent(this.onAddToCartButtonClickEventName);
		this.openQuickView(style.partNumber, index);
	}

	/**
	 * @description
	 * Event handler for the QuickView "ATC Success Event".  It will emit an Event
	 * on the Event Bus to be picked up by any listeners for additional processing.
	 */
	onQuickViewAddToCartSuccess() {
		this.eventBusService.emit(new EmitEvent(Events.PostATCEvent));
	}

	/**
	 * @description
	 * Event handler for when the recommendation product card link is clicked. It will
	 * trigger an Event to analytics and the re-assigns window.location
	 *
	 * @param style -- The recommended product style document
	 * @param index -- The index location of the product in the list
	 */
	onRecommendationLinkClick(style: IElasticProductDetailsStyle, index: any) {
		// Trigger analytics Event
		this.analyticsService.triggerAnalyticsEvent(this.onRecommendationLinkClickEventName);
		let pagetype = this.pageType;
		// Ensure the PDP URL exists
		if (typeof style.pdpSeoUrl === 'string' && style.pdpSeoUrl.trim() !== '') {
			if (
				this.pageType == ProductRecommendationsPageType.PDP &&
				this.containerID === ContainerId.PLA &&
				this.hideAddToCart
			) {
				pagetype = this.dsPDPlanding;
			} else if (
				this.pageType == ProductRecommendationsPageType.PDP &&
				this.containerID === ContainerId.YMAL &&
				this.hideAddToCart
			) {
				pagetype = this.dsPDPYmal;
			} else if (this.pageType == ProductRecommendationsPageType.CART && this.productRecommendationsQuickViewEnabled) {
				this.openQuickView(style.partNumber, index);
				return;
			}
			// Build query params for analytics tracking
			const params = `recid=${this.buildRecID(pagetype, index)}`;
			// Assign window.location
			this.window.location.assign(
				`${style.pdpSeoUrl}${style.pdpSeoUrl.includes('?') ? '&' : '?'}${params}`
			);
		}
	}

	/**
	 * @description
	 * determines if the device is mobile based on breakpoints when the window resizes
	 */
	handleResizeEvent(isMobileBreakpoint: boolean): void {
		this.isMobileDevice(isMobileBreakpoint);
	}

	mapItemToProductCardData(item: any){
		if (!item) return null;
		return {
			assetSeoUrl: item.pdpSeoUrl,
			ecode: item.catentryId,
			imageData: {
				imageUrl: item.image
			},
			pricing: {
				prices: {
					maxListPrice: (item)?.prices?.maxlistprice,
					maxOfferPrice: (item)?.prices?.maxofferprice,
					minListPrice: (item)?.prices?.minlistprice,
					minOfferPrice: (item)?.prices?.minofferprice,
					maxMapPrice: (item)?.prices?.maxmapprice
				}
			},
			productName: item.name
		};
	}

	goToRecommendedForYouDS(): void {
		// Go to Recommended For You page /s/recommended-for-you
		this.window.location.href = '/s/recommended-for-you';
	}

	getProductCardSize(): 'small' | 'medium' | 'large' {
		if (this.cardSize) {
			return this.cardSize;
		}
		if (this.chain.chainIdentifierAbbr === 'pl') return this.isMobile ? 'medium' : 'large';
		return this.isMobile ? 'small' : 'medium';
	}

	getLoadingStrategy(): string {
		return this.isPLA ? LoadingStrategy.Eager : LoadingStrategy.Lazy;
	}

	getFetchPriority(index:number): string {
		return this.isPLA && index === 0 && this.isMobile ? FetchPriority.High : FetchPriority.Auto;
	}

	private isMobileDevice(isMobileBreakpoint?: boolean): void {
		this.isMobile =
			isMobileBreakpoint ??
			[BreakPoint.EXTRASMALL, BreakPoint.SMALL, BreakPoint.MEDIUM].includes(
				this.deviceService.getDeviceBreakPoint(true)
			);
	}

	private openQuickView(partNumber: string, index: number) {
		const dataAndCallBack = {
			additionalData: {
				DSDiversityLevel: this.dsRecsDiversityLevel,
				DSDisplayName: this.displayName,
				DSPlacementId: this.placementID,
				DSPosition: index,
				DSPriceRerankLevel: this.priceRerankLevel,
				DSRecsGCPProject: this.dsRecsGCPProject
			},
			callback: this.onQuickViewAddToCartSuccess.bind(this)
		};
		this.window.quickview?.openProductQuickView(partNumber, 'DataScienceRecs', dataAndCallBack);
	}

	/**
	 * @description
	 * Checks localStorage to see if the AthleteID exists and will return it if so,
	 * otherwise returns a default value of 'XXXXX'
	 *
	 * @private
	 */
	private getAthleteID(): string | undefined {
		// The AthleteID to return, defaulted to the fallback value
		let athleteID = this.utilityService.generateUUID();
		if (this.hasGuidVisitor) {
			return athleteID;
		}
		// Get the analytics IDs from localStorage
		const tagIDs: any = JSON.parse(this.localStorageService.get('TAG_IDs') || '{}');

		// Check to see if the AthleteID is actually set or if empty
		if (typeof tagIDs.AthleteID?.ID === 'string' && tagIDs.AthleteID.ID.trim() !== '') {
			athleteID = tagIDs.AthleteID.ID;
		} else if (
			typeof tagIDs.AdobeMCVID?.ID === 'string' &&
			tagIDs.AdobeMCVID.ID.trim() !== ''
		) {
			athleteID = tagIDs.AdobeMCVID.ID;
		}
		// Return the AthleteID
		return athleteID;
	}

	/**
	 * @description
	 * Does any product transformations before returning the results to be displayed in the template
	 *
	 * @param recommendations -- The recommendations to parse for display
	 */
	private parseRecommendations(
		recommendations: IElasticProductDetails[]
	): IElasticProductDetailsStyle[] {
		// Return array
		const returnRecommendations: IElasticProductDetailsStyle[] = [];

		// Iterate through the recommendations
		if (Array.isArray(recommendations) && recommendations?.length > 0) {
			recommendations.forEach((recommendation: IElasticProductDetails) => {
				// Get the default color if it is present
				const defaultColor =
					typeof recommendation.style.defaultColor === 'string' &&
					recommendation.style.defaultColor.trim() !== ''
						? `${recommendation.style.defaultColor}`
						: '';

				// Set the image property on the style
				recommendation.style.image = this.scene7Service.getProductImageUrl(
					recommendation.style.partNumber,
					'itembean',
					defaultColor,
					'$UTPMain$',
					undefined,
					false
				);

				// Add to the style to the display list
				returnRecommendations.push(recommendation.style);
			});
		}

		// Return the recommendations
		return returnRecommendations;
	}

	private refreshRecommendations() {
		const purchaseTransaction = {} as IRecommendationsRequestPurchaseTransaction;
		if (this.eventType === 'purchase-complete') {
			purchaseTransaction.id = this.orderPricing.external_key;
			purchaseTransaction.revenue = this.orderPricing.total;
			purchaseTransaction.currencyCode = this.orderPricing.currency;
			if (this.addTaxAndCost) {
				purchaseTransaction.tax = this.orderPricing.total_tax;
				purchaseTransaction.cost = this.orderPricing.total;
			}
		}

		// Get recommendations
		this.recommendationsService
			.getRecommendationsWithProductDetails$(
				this.eCodeList,
				this.placementID,
				this.dsFilter,
				this.priceRerankLevel,
				this.dsRecsDiversityLevel,
				this.eventType,
				this.athleteID,
				this.dsRecsProductDisplayLimit,
				this.dsRecsGCPProject,
				purchaseTransaction,
				this.pageType,
				this.containerID
			)
			.subscribe({
				next: (recommendations: IElasticProductDetails[]) => {
					// Parse the recommendations
					this.productRecommendations = this.parseRecommendations(recommendations);

					// Emit the event that recommendations were loaded
					this.recommendationsLoaded.emit(true);
				},
				error: () => {
					// Set error for analytics Event
					const error =
						'Error getting DS Recommendations, not displaying any recommendations';
					this.analyticsService.setErrorId(this.onRecommendationsDisplayErrorEventName);
					this.analyticsService.setErrorMessage(`CART_RECOMMENDATIONS:${error}`);

					this.productRecommendations = [];
					this.recommendationsErrored.emit();

					// Trigger analytics Event
					this.analyticsService.triggerAnalyticsEvent(
						this.onRecommendationsDisplayErrorEventName,
						{
							ErrorMessage: `CART_RECOMMENDATIONS: ${error}`,
							PageType: this.pageType || 'Cart'
						}
					);
				}
			});
	}

	private buildRecID(pageType: string, productIndex: number): string {
		const linkID = (this.customLinkID !== '') ? this.customLinkID : `DS_${pageType}_${this.placementID}`;
		return `${linkID}_${productIndex}`;
	}
	
	protected trackByName(index: number, item: any): string {
		// Need to check for .name because skeleton loader requires null values and name wont exist on these values.
		return index.toString() + (item?.name ? item.name : '');
	}
}